/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */
import React, { useState, useEffect } from "react";
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Pagination,
    PaginationItem,
} from "@mui/material";
import { ReactComponent as PreviousIcon } from "assets/images/icons/previous.svg";
import { ReactComponent as NextIcon } from "assets/images/icons/next.svg";
import Dropdown from "./dropdown";
import { columns } from "./columns.js";

const Table = (props) => {
    const [tableData, setTableData] = useState([]);
    
    // Use props for pagination instead of local state
    const currentPageNumber = props?.currentPage || 1;
    const itemsPerPage = props?.pageSize || 10;
    const totalItemCount = props?.pagination?.total || 0;

    // Helper functions for CSS class names
    const getRowClassName = (rowIndex) => {
        return rowIndex % 2 === 0 ? "table-row-even" : "table-row-odd";
    };

    const getHeaderCellClassName = (columnIndex, totalColumns) => {
        const baseClass = "table-header-cell";
        const borderClass = columnIndex < totalColumns - 1 ? "has-border-right" : "";
        return `${baseClass} ${borderClass}`.trim();
    };

    const getBodyCellClassName = (columnIndex, totalColumns) => {
        const baseClass = "table-body-cell";
        const borderClass = columnIndex < totalColumns - 1 ? "has-border-right" : "";
        return `${baseClass} ${borderClass}`.trim();
    };

    // Use data directly from props (server-side pagination)
    useEffect(() => {
        setTableData(props?.data || []);
    }, [props?.data]);

    const handlePageChange = (event, newPageNumber) => {
        props?.onPageChange?.(event, newPageNumber);
    };

    const handleItemsPerPageChange = (event) => {
        props?.onPageSizeChange?.(event);
    };

    const totalPageCount = Math.ceil(totalItemCount / itemsPerPage);

    // Function to render evidence
    const renderEvidenceLinks = (evidenceArray) => {
        if (!evidenceArray || evidenceArray.length === 0) {
            return "";
        }
        
        return (
            <Box className="evidence-container">
                {evidenceArray?.map((evidenceItem, itemIndex) => {
                    return (
                        <Box 
                            key={`${evidenceItem?.filename}-${itemIndex}`}
                            component="a"
                            href={evidenceItem?.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="evidence-link"
                        >
                            {evidenceItem?.title || evidenceItem?.filename}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // Function to format date and time
    const formatDateTime = (dataRow, fieldType) => {
        if (fieldType === "date") {
            const dateValue = dataRow?.date;
            if (dateValue) {
                // If date is already in YYYY-MM-DD format, return as is
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                    return dateValue;
                }
                // Otherwise, try to parse and format
                const date = new Date(dateValue);
                return date.toLocaleDateString("en-CA"); // YYYY-MM-DD format
            }
            return "N/A";
        } else if (fieldType === "time") {
            const timeValue = dataRow?.time;
            if (timeValue) {
                // If time is already in HH:MM:SS format, convert to 12-hour format
                if (/^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
                    const [hours, minutes] = timeValue.split(':');
                    const hour12 = parseInt(hours) % 12 || 12;
                    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                    return `${hour12}:${minutes} ${ampm}`;
                }
                // Otherwise, try to parse as full datetime
                const date = new Date(timeValue);
                const timeString = date.toLocaleTimeString("en-CA", { 
                    hour: "2-digit", 
                    minute: "2-digit", 
                    hour12: true 
                });
                return timeString.replace(/a\.m\./gi, "AM").replace(/p\.m\./gi, "PM");
            }
            return "N/A";
        }
        return dataRow?.[fieldType] || "N/A";
    };


    return (
        <div className="simplicity-table">
            <Paper className="table-paper-container">
                <TableContainer className="table-container">
                    <MuiTable 
                        stickyHeader
                        className="table-base"
                    >
                        <TableHead>
                            <TableRow>
                                {columns?.map((columnDefinition, columnIndex) => (
                                    <TableCell
                                        key={columnDefinition?.id}
                                        className={getHeaderCellClassName(columnIndex, columns?.length || 0)}
                                        style={{ 
                                            minWidth: columnDefinition?.minWidth,
                                            maxWidth: columnDefinition?.maxWidth
                                        }}
                                    >
                                        {columnDefinition?.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(tableData?.length || 0) === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns?.length || 0} align="center">
                                        <Typography>No data found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableData?.map((dataRow, rowIndex) => (
                                    <TableRow 
                                        key={dataRow?.id}
                                        className={getRowClassName(rowIndex)}
                                    >
                                        {columns?.map((columnDefinition, columnIndex) => (
                                            <TableCell 
                                                key={columnDefinition?.id}
                                                className={getBodyCellClassName(columnIndex, columns?.length || 0)}
                                                style={{
                                                    minWidth: columnDefinition?.minWidth,
                                                    maxWidth: columnDefinition?.maxWidth
                                                }}
                                            >
                                                {columnDefinition?.id === "evidence" ? 
                                                    renderEvidenceLinks(dataRow?.[columnDefinition?.id]) : 
                                                    (columnDefinition?.id === "date" || columnDefinition?.id === "time") ?
                                                        formatDateTime(dataRow, columnDefinition?.id) :
                                                        dataRow?.[columnDefinition?.id]
                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </MuiTable>
                </TableContainer>
                
                {/* Pagination Controls */}
                <Box className="pagination-container">
                    
                    <Box className="pagination-wrapper">
                        <Pagination
                            count={totalPageCount}
                            page={currentPageNumber}
                            onChange={handlePageChange}
                            color="primary"
                            siblingCount={2}
                            boundaryCount={1}
                            className="pagination-base"
                            renderItem={(item) => (
                                <PaginationItem
                                    slots={{
                                        previous: () => (
                                            <Box className="pagination-button-base">
                                                <PreviousIcon />
                                            </Box>
                                        ),
                                        next: () => (
                                            <Box className="pagination-button-base">
                                                <NextIcon />
                                            </Box>
                                        )
                                    }}
                                    {...item}
                                    className={`pagination-item ${item.selected ? "pagination-selected" : ""}`}
                                />
                            )}
                        />
                        <Box className="items-per-page-container">
                            <Typography component="div" className="items-per-page-label">
                                Items per page:
                            </Typography>
                            <Dropdown
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                options={[
                                    { value: 5, label: "5" },
                                    { value: 10, label: "10" },
                                    { value: 25, label: "25" },
                                    { value: 50, label: "50" }
                                ]}
                                fullWidth={false}
                                width="86px"
                            />
                        </Box>
                        
                        
                    </Box>
                </Box>
            </Paper>
        </div>
    );

};

Table.displayName = "Table";
export { Table };
